// apps/web/src/features/russia-requests/components/tabs/CarriersTab.tsx
//
// #1167 Russia carriers tab — flat carrier list. #1580 adds a
// per-departure-city rates section above it when the request carries start
// cities.
//
// Layout:
//   - Header bar: completed count + "Add carrier" trigger
//   - #1580: per-city rates section (one section per RussiaRequestStartCity)
//     when start cities exist
//   - Single list of carriers; each card renders displayCode + name +
//     contacts (PII-gated by the wire), state pill, and all bids.
//
// @aiPurpose owns the modal arbitration. Children stay pure
//   presentational; mutations fan out via `useAddRussiaRequestCarrier`
//   and `useAddRussiaRequestBid`.
// @aiSecurity write gate is `russiaCarriers.manage` (#1167 — was
//   `russiaRequests.manage` before). Add-carrier is gated to
//   READY_FOR_WORK ∪ IN_WORK (the first add auto-starts the request);
//   bid / carrier edit + delete stay gated to IN_WORK only (#1544).
//   The manager-side winner pick is gated to `canPickRussiaWinner` ×
//   IN_WORK ∪ PROCESSED.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RequestStatus,
  hasPermission,
  type RussiaRequest,
  type RussiaRequestCarrier,
  type RussiaRequestCarrierBid,
} from '@magimp/shared';

import { useAuthStore } from '../../../../stores/auth.js';
import { Button, ConfirmModal } from '../../../../ui/index.js';
import { canPickRussiaWinner } from '../../role.js';
import {
  useDeleteRussiaRequestBid,
  useDeleteRussiaRequestCarrier,
  useRussiaRequestCarriers,
  useRussiaRequestPickWinner,
} from '../../queries.js';
import { RussiaAddCarrierModal } from './RussiaAddCarrierModal.js';
import { RussiaCarrierBidCard } from './RussiaCarrierBidCard.js';
import { RussiaCarriersBidFormModal } from './RussiaCarriersBidFormModal.js';
import { RussiaEditCarrierModal } from './RussiaEditCarrierModal.js';
import { RussiaPerCityRates, type PerCityBidTarget } from './RussiaPerCityRates.js';

type BidModalState = {
  carrierId: string;
  carrierName: string;
  bid?: RussiaRequestCarrierBid;
  // WHY (#1580): when set, the bid modal submits a per-departure-city
  //   (START_CITY) rate scoped to this arrivalCityId.
  startCityRefId?: string;
} | null;
type EditCarrierState = { carrier: RussiaRequestCarrier } | null;
type DeleteCarrierState = { carrierId: string; displayCode: string } | null;
type DeleteBidState = { carrierId: string; bidId: string } | null;

export function CarriersTab({ request }: { request: RussiaRequest }) {
  const { t } = useTranslation('russiaRequests');
  const query = useRussiaRequestCarriers(request.id);
  const permissions = useAuthStore((s) => s.permissions);

  const items: ReadonlyArray<RussiaRequestCarrier> = query.data?.items ?? [];

  // WHY (#1167/#1544): write gate is `russiaCarriers.manage`. Since #1544
  //   `send` lands in READY_FOR_WORK, the first carrier-add auto-promotes
  //   READY_FOR_WORK → IN_WORK (backend `addCarrier` accepts both states),
  //   so the Add-Carrier affordance must light up in READY_FOR_WORK too —
  //   otherwise a just-sent request strands the logist with no CTA.
  const canManage = hasPermission(permissions, 'russiaCarriers.manage');
  const canAddCarrier =
    canManage &&
    (request.status === RequestStatus.READY_FOR_WORK || request.status === RequestStatus.IN_WORK);
  // WHY (#1544): bid add/edit/delete and carrier edit/delete require
  //   IN_WORK on the backend; a READY_FOR_WORK request has no carriers/bids
  //   yet and the first add auto-promotes to IN_WORK, so these stay
  //   IN_WORK-only.
  const canEditCarriers = canManage && request.status === RequestStatus.IN_WORK;

  // WHY (#1544): manager-side winner pick is operable from IN_WORK
  //   (pre-pick) through PROCESSED (where `success` consumes the stored
  //   winner). Authority is manager/admin only — the backend rejects a
  //   logist winner-pick with 403.
  // WHY (#1837): the window also covers the REFUSED / POSTPONED revive
  //   states so the manager can re-pick a winner before reviving the row to
  //   SUCCESS — but ONLY while no request-level winner is stored. A REFUSED
  //   row always cleared its winner; a POSTPONED row preserves it, so the
  //   `winnerBidId === null` guard hides the pick control on a parked row
  //   that must REUSE its winner (the backend rejects a re-pick there too).
  const canPickWinner =
    canPickRussiaWinner(permissions) &&
    (request.status === RequestStatus.IN_WORK ||
      request.status === RequestStatus.PROCESSED ||
      ((request.status === RequestStatus.REFUSED || request.status === RequestStatus.POSTPONED) &&
        request.winnerBidId === null));

  const [addCarrierOpen, setAddCarrierOpen] = useState(false);
  const [bidModal, setBidModal] = useState<BidModalState>(null);
  const [editCarrier, setEditCarrier] = useState<EditCarrierState>(null);
  const [deleteCarrier, setDeleteCarrier] = useState<DeleteCarrierState>(null);
  const [deleteBid, setDeleteBid] = useState<DeleteBidState>(null);

  const deleteCarrierMutation = useDeleteRussiaRequestCarrier(request.id);
  const deleteBidMutation = useDeleteRussiaRequestBid(request.id);
  const pickWinnerMutation = useRussiaRequestPickWinner(request.id);

  if (query.isLoading) {
    return (
      <p data-testid="russia-request-drawer-carriers-loading" style={{ fontSize: 13, margin: 0 }}>
        {t('loading')}
      </p>
    );
  }
  if (query.isError) {
    return (
      <p
        data-testid="russia-request-drawer-carriers-error"
        role="alert"
        style={{ fontSize: 13, margin: 0 }}
      >
        {t('drawer.carriers.error')}
      </p>
    );
  }

  // WHY (#1058): "completed response" is ANSWERED + REJECTED — mirrors
  //   the China carriers-tab counter.
  const completedCount = items.filter(
    (c) => c.state === 'ANSWERED' || c.state === 'REJECTED',
  ).length;

  // WHY (#1580): a per-city add/edit reuses the bid modal but pins the
  //   START_CITY scope to the target start city.
  const openPerCityRate = (target: PerCityBidTarget) =>
    setBidModal({
      carrierId: target.carrierId,
      carrierName: target.carrierName,
      startCityRefId: target.arrivalCityId,
      ...(target.bid ? { bid: target.bid } : {}),
    });

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {(items.length > 0 || canAddCarrier) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: 10,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: 'var(--fg-3)',
            }}
          >
            {t('drawer.carriers.section.bestOffers')}
          </span>
          <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
            {t('drawer.carriers.section.completedCount', {
              completed: completedCount,
              total: items.length,
            })}
          </span>
          {canAddCarrier && (
            <Button
              kind="primary"
              size="sm"
              icon="plus"
              data-testid="russia-request-drawer-carriers-add-trigger"
              onClick={() => setAddCarrierOpen(true)}
              style={{ marginLeft: 'auto' }}
            >
              {t('drawer.carriers.add.trigger')}
            </Button>
          )}
        </div>
      )}

      {/* #1580: per-departure-city rates. Renders only when the request
          carries start cities; one section per city with its bids, an
          add-rate CTA per carrier missing a rate, and a winner pick. */}
      {items.length > 0 && (
        <RussiaPerCityRates
          request={request}
          carriers={items}
          canEdit={canEditCarriers}
          canPickWinner={canPickWinner}
          onAddOrEditRate={openPerCityRate}
          onPickWinner={(b) => pickWinnerMutation.mutate({ bidId: b.id })}
        />
      )}

      {items.length === 0 ? (
        <p
          data-testid="russia-request-drawer-carriers-empty"
          style={{ color: 'var(--fg-3)', fontSize: 13, margin: 0 }}
        >
          {t('drawer.carriers.empty')}
        </p>
      ) : (
        <ul
          data-testid="russia-request-drawer-carriers-list"
          style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}
        >
          {items.map((c) => (
            <li key={c.id}>
              <RussiaCarrierBidCard
                carrier={c}
                canEdit={canEditCarriers}
                canPickWinner={canPickWinner}
                winnerBidId={request.winnerBidId}
                onPickWinner={(b) => pickWinnerMutation.mutate({ bidId: b.id })}
                onAddBid={() =>
                  setBidModal({
                    carrierId: c.id,
                    // WHY (#1167 PII): `nameSnapshot` may be omitted for
                    //   manager actors. The modal title falls back to
                    //   the displayCode so identity never leaks.
                    carrierName: c.nameSnapshot ?? c.displayCode,
                  })
                }
                onEditCarrier={() => setEditCarrier({ carrier: c })}
                onDeleteCarrier={() =>
                  setDeleteCarrier({ carrierId: c.id, displayCode: c.displayCode })
                }
                onEditBid={(b) =>
                  setBidModal({
                    carrierId: c.id,
                    carrierName: c.nameSnapshot ?? c.displayCode,
                    bid: b,
                  })
                }
                onDeleteBid={(b) => setDeleteBid({ carrierId: c.id, bidId: b.id })}
              />
            </li>
          ))}
        </ul>
      )}

      {addCarrierOpen && (
        <RussiaAddCarrierModal
          russiaRequestId={request.id}
          onClose={() => setAddCarrierOpen(false)}
        />
      )}
      {bidModal && (
        <RussiaCarriersBidFormModal
          russiaRequestId={request.id}
          russiaRequestCarrierId={bidModal.carrierId}
          carrierName={bidModal.carrierName}
          {...(bidModal.bid ? { bid: bidModal.bid } : {})}
          {...(bidModal.startCityRefId ? { startCityRefId: bidModal.startCityRefId } : {})}
          onClose={() => setBidModal(null)}
        />
      )}
      {editCarrier && (
        <RussiaEditCarrierModal
          russiaRequestId={request.id}
          carrier={editCarrier.carrier}
          onClose={() => setEditCarrier(null)}
        />
      )}
      {deleteCarrier && (
        <ConfirmModal
          danger
          title={t('drawer.carriers.deleteCarrier.confirmTitle', {
            code: deleteCarrier.displayCode,
          })}
          text={t('drawer.carriers.deleteCarrier.confirmBody')}
          onClose={() => setDeleteCarrier(null)}
          onConfirm={() => {
            const target = deleteCarrier;
            deleteCarrierMutation.mutate({ russiaRequestCarrierId: target.carrierId });
          }}
        />
      )}
      {deleteBid && (
        <ConfirmModal
          danger
          title={t('drawer.carriers.deleteBid.confirmTitle')}
          text={t('drawer.carriers.deleteBid.confirmBody')}
          onClose={() => setDeleteBid(null)}
          onConfirm={() => {
            const target = deleteBid;
            deleteBidMutation.mutate({
              russiaRequestCarrierId: target.carrierId,
              bidId: target.bidId,
            });
          }}
        />
      )}
    </div>
  );
}
